import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  Button, 
  Link, 
  ButtonGroup, 
  Container,
  useScrollTrigger,
  Slide,
  Avatar,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { 
  GitHub as GitHubIcon, 
  Home as HomeIcon, 
  Apps as AppsIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';

interface HeaderProps {
  onNavigateHome?: () => void;
  onNavigateApp?: () => void;
  onNavigateTutorial?: () => void;
  currentPage?: 'home' | 'app' | 'tutorial';
}

// Hide AppBar on scroll down
function HideOnScroll(props: { children: React.ReactElement }) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Header: React.FC<HeaderProps> = ({ 
  onNavigateHome, 
  onNavigateApp, 
  onNavigateTutorial,
  currentPage = 'home' 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 250, height: '100%', bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={handleDrawerToggle} color="inherit">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
        <Avatar 
          src="/nft-preview.png" 
          alt="NFT Art Engine" 
          sx={{ 
            width: 80, 
            height: 80, 
            mb: 2,
            boxShadow: 'none',
            borderRadius: 2,
            background: 'transparent',
          }}
          variant="square"
        />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          NFT Art Engine
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          Powered by HashLips
        </Typography>
      </Box>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              onNavigateHome?.();
              handleDrawerToggle();
            }}
            selected={currentPage === 'home'}
            sx={{
              my: 1,
              borderRadius: 2,
              mx: 1,
              '&.Mui-selected': {
                bgcolor: 'rgba(98, 0, 234, 0.15)',
                '&:hover': {
                  bgcolor: 'rgba(98, 0, 234, 0.25)',
                }
              }
            }}
          >
            <ListItemIcon>
              <HomeIcon color={currentPage === 'home' ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              onNavigateApp?.();
              handleDrawerToggle();
            }}
            selected={currentPage === 'app'}
            sx={{
              my: 1,
              borderRadius: 2,
              mx: 1,
              '&.Mui-selected': {
                bgcolor: 'rgba(98, 0, 234, 0.15)',
                '&:hover': {
                  bgcolor: 'rgba(98, 0, 234, 0.25)',
                }
              }
            }}
          >
            <ListItemIcon>
              <AppsIcon color={currentPage === 'app' ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="App" />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              onNavigateTutorial?.();
              handleDrawerToggle();
            }}
            selected={currentPage === 'tutorial'}
            sx={{
              my: 1,
              borderRadius: 2,
              mx: 1,
              '&.Mui-selected': {
                bgcolor: 'rgba(98, 0, 234, 0.15)',
                '&:hover': {
                  bgcolor: 'rgba(98, 0, 234, 0.25)',
                }
              }
            }}
          >
            <ListItemIcon>
              <MenuBookIcon color={currentPage === 'tutorial' ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Tutorial" />
          </ListItemButton>
        </ListItem>
        
        <Divider sx={{ my: 2 }} />
        
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            href="https://github.com/hashlips/hashlips_art_engine"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              my: 1,
              borderRadius: 2,
              mx: 1
            }}
          >
            <ListItemIcon>
              <GitHubIcon />
            </ListItemIcon>
            <ListItemText primary="GitHub" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          elevation={scrolled ? 4 : 0}
          sx={{ 
            background: scrolled 
              ? `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` 
              : 'transparent',
            backdropFilter: scrolled ? 'blur(10px)' : 'none',
            transition: 'all 0.3s ease-in-out',
            boxShadow: scrolled 
              ? '0 4px 20px rgba(0, 0, 0, 0.2)' 
              : 'none',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ py: scrolled ? 0.5 : 1 }}>
              {/* Logo and Title */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flexGrow: 1,
                  cursor: 'pointer'
                }}
                onClick={onNavigateHome}
              >
                <Avatar 
                  src="/nft-preview.png" 
                  alt="NFT Art Engine" 
                  sx={{ 
                    width: scrolled ? 40 : 48, 
                    height: scrolled ? 40 : 48, 
                    mr: 2,
                    transition: 'all 0.3s ease-in-out',
                    borderRadius: 0,
                    background: 'transparent',
                    boxShadow: 'none'
                  }}
                  variant="square"
                />
                <Box>
                  <Typography 
                    variant={scrolled ? "h6" : "h5"} 
                    component="div" 
                    sx={{ 
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease-in-out',
                      background: `linear-gradient(90deg, #ffffff, ${theme.palette.secondary.light})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    NFT Art Engine
                  </Typography>
                  <Typography 
                    variant="caption" 
                    component="div" 
                    sx={{ 
                      fontSize: '0.7rem',
                      opacity: 0.8
                    }}
                  >
                    Powered by HashLips
                  </Typography>
                </Box>
              </Box>
              
              {/* Desktop Navigation */}
              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    color="inherit" 
                    sx={{ 
                      mr: 3, 
                      display: { xs: 'none', sm: 'block' },
                      opacity: 0.9
                    }}
                  >
                    Create your NFT collection easily
                  </Typography>
                  
                  {/* Navigation Buttons */}
                  <ButtonGroup 
                    variant="contained" 
                    sx={{ 
                      mr: 3,
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      overflow: 'hidden',
                      '& .MuiButton-root': {
                        borderColor: 'rgba(255,255,255,0.05)',
                        px: 2
                      }
                    }}
                  >
                    <Button 
                      color="inherit" 
                      startIcon={<HomeIcon />}
                      onClick={onNavigateHome}
                      sx={{ 
                        bgcolor: currentPage === 'home' ? 'rgba(255,255,255,0.2)' : 'transparent',
                        '&:hover': {
                          bgcolor: currentPage === 'home' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'
                        },
                        borderRadius: '8px 0 0 8px',
                        position: 'relative',
                        '&::after': currentPage === 'home' ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '10%',
                          width: '80%',
                          height: '3px',
                          background: theme.palette.secondary.main,
                          borderRadius: '3px 3px 0 0'
                        } : {}
                      }}
                    >
                      Home
                    </Button>
                    <Button 
                      color="inherit" 
                      startIcon={<AppsIcon />}
                      onClick={onNavigateApp}
                      sx={{ 
                        bgcolor: currentPage === 'app' ? 'rgba(255,255,255,0.2)' : 'transparent',
                        '&:hover': {
                          bgcolor: currentPage === 'app' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'
                        },
                        position: 'relative',
                        '&::after': currentPage === 'app' ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '10%',
                          width: '80%',
                          height: '3px',
                          background: theme.palette.secondary.main,
                          borderRadius: '3px 3px 0 0'
                        } : {}
                      }}
                    >
                      App
                    </Button>
                    <Button 
                      color="inherit" 
                      startIcon={<MenuBookIcon />}
                      onClick={onNavigateTutorial}
                      sx={{ 
                        bgcolor: currentPage === 'tutorial' ? 'rgba(255,255,255,0.2)' : 'transparent',
                        '&:hover': {
                          bgcolor: currentPage === 'tutorial' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'
                        },
                        borderRadius: '0 8px 8px 0',
                        position: 'relative',
                        '&::after': currentPage === 'tutorial' ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '10%',
                          width: '80%',
                          height: '3px',
                          background: theme.palette.secondary.main,
                          borderRadius: '3px 3px 0 0'
                        } : {}
                      }}
                    >
                      Tutorial
                    </Button>
                  </ButtonGroup>
                  
                  <Button 
                    component={Link}
                    href="https://github.com/hashlips/hashlips_art_engine" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    color="inherit"
                    startIcon={<GitHubIcon />}
                    variant="outlined"
                    sx={{ 
                      borderRadius: 2,
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.8)',
                        bgcolor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    GitHub
                  </Button>
                </Box>
              )}
              
              {/* Mobile Menu Button */}
              {isMobile && (
                <IconButton 
                  color="inherit" 
                  aria-label="open drawer" 
                  edge="end" 
                  onClick={handleDrawerToggle}
                  sx={{ 
                    ml: 2,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      
      {/* Add toolbar spacing */}
      <Toolbar sx={{ mb: 1 }} />
      
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 250,
            boxShadow: '0 0 20px rgba(0,0,0,0.3)'
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header; 